import { NgIconButton } from "@/atoms/buttons";
import { CustomCloseIcon } from "@/icons";

import { cn } from "@oneleet/utils";
import { PolymorphicPropsWithoutRef } from "@oneleet/utils/src/polymorphism";

import * as Dialog from "@radix-ui/react-dialog";
import { type VariantProps, cva } from "class-variance-authority";
import { motion } from "framer-motion";

const DEFAULT_SIZE = "md";

const modalContainer = cva(
  "relative w-full overflow-hidden rounded-lg bg-white p-0 shadow-[0px_4px_6px_-1px_rgba(0,0,0,0.10),_0px_2px_4px_-1px_rgba(0,0,0,0.06)] sm:mx-6 sm:my-8",
  {
    variants: {
      size: {
        xs: "max-w-xs",
        sm: "max-w-sm",
        md: "max-w-md",
        lg: "max-w-lg",
        xl: "max-w-xl",
        "2xl": "max-w-2xl",
        "3xl": "max-w-3xl",
        "4xl": "max-w-4xl",
      },
    },
    defaultVariants: {
      size: DEFAULT_SIZE,
    },
  },
);

type ModalSizeVariants = VariantProps<typeof modalContainer>;

export type ModalProps = {
  showCloseButton?: boolean;
  panelPosition?: "start" | "center";
  children?: React.ReactNode;
  className?: string;
  layoutAnimation?: boolean;
  contentProps?: React.HTMLAttributes<HTMLDivElement>;
  classNames?: {
    contentWrap?: string;
    dialog?: string;
    inner?: string;
  };
  onClose?: (e?: React.MouseEvent<Element, MouseEvent>) => void;
} & Omit<ModalSizeVariants, "className">;

const ANIMATION_ENTER_TIME_SECONDS = 0.05;
const ANIMATION_EXIT_TIME_SECONDS = 0.1;

export const NgModal = <T extends React.ElementType>({
  size = DEFAULT_SIZE,
  showCloseButton = false,
  panelPosition = "center",
  children,
  className,
  layoutAnimation = false,
  classNames,
  contentProps,
  onClose = () => null,
  ...rest
}: PolymorphicPropsWithoutRef<ModalProps, T>) => {
  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()} {...rest}>
      <Dialog.Portal forceMount>
        <Dialog.Overlay asChild>
          <motion.div
            className="fixed inset-0 z-[90] bg-black bg-opacity-50"
            initial={{
              opacity: 0,
            }}
            animate={{
              opacity: 0.75,
              transition: {
                duration: ANIMATION_ENTER_TIME_SECONDS,
              },
            }}
            exit={{
              opacity: 0,
              transition: {
                duration: ANIMATION_EXIT_TIME_SECONDS,
              },
            }}
          />
        </Dialog.Overlay>
        <div className="fixed inset-0 z-[90] overflow-y-auto">
          <div
            className={cn(
              "flex min-h-full justify-center p-4 sm:p-2",
              panelPosition === "start" ? "items-start" : "items-center",
              classNames?.contentWrap,
            )}
          >
            <Dialog.Content
              asChild
              // Prevent auto-focusing the close button on open
              onOpenAutoFocus={(e: Event) => e.preventDefault()}
              {...contentProps}
            >
              <motion.div
                className={cn(
                  modalContainer({ size, className: classNames?.dialog }),
                )}
                initial={{
                  opacity: 0,
                  scale: 0.95,
                }}
                animate={{
                  opacity: 1,
                  y: 0,
                  scale: 1,
                  transition: {
                    duration: ANIMATION_ENTER_TIME_SECONDS,
                  },
                }}
                exit={{
                  opacity: 0,
                  scale: 0.95,
                  transition: {
                    duration: ANIMATION_EXIT_TIME_SECONDS,
                  },
                }}
                layout={layoutAnimation}
              >
                {/* Silences an annoying Sentry warning about a missing dialog description. */}
                <Dialog.Description className="sr-only">
                  Modal content
                </Dialog.Description>
                <div
                  className={cn(
                    "w-full px-4 pb-4 pt-5 sm:p-8",
                    classNames?.inner,
                  )}
                >
                  {children}
                </div>
                {showCloseButton && (
                  <NgModal.CloseButton
                    className="absolute right-4 top-4"
                    onClick={onClose}
                  />
                )}
              </motion.div>
            </Dialog.Content>
          </div>
        </div>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

function ModalTitle({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <Dialog.Title
      className={cn(
        // Do not add margin or padding here. This should be handled outside of this component wherever it's used.
        "text-heading-lg text-neutral-950",
        className,
      )}
    >
      {children}
    </Dialog.Title>
  );
}

NgModal.Title = ModalTitle;

export function CloseButton({
  className,
  onClick,
}: {
  className?: string;
  onClick: () => void;
}) {
  return (
    <Dialog.Close asChild>
      <NgIconButton
        className={cn("p-1", className)}
        icon={<CustomCloseIcon className="size-6" />}
        size="large"
        variant="plain"
        color="neutral"
        onClick={onClick}
      />
    </Dialog.Close>
  );
}

NgModal.CloseButton = CloseButton;
